/* ComboBoxControlRenderer.js */

"use strict";

const _ = require("lodash");
const BrowserDetect = require("@gluenetworks/srvc_core_browser-detect");
const InputSearchControl = require("@gluenetworks/input_search_control");
const SelectListControl = require("@gluenetworks/select_list").Single;
const MultipleSelectListControl = require("@gluenetworks/select_list").Multiple;

class ComboBoxControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("combo-box-control");
        this._rootElem.tabIndex = 0;
        this._rootElem.component = this._model;
        this._renderSelectList();
        this._selectionDisplay = document.createElement("div");
        this._selectionDisplay.classList.add("selection-display");
        this._optionContainer = document.createElement("div");
        this._optionContainer.classList.add("option-container");

        this._selectBoxForMobile = {};
        this._previousValue = "";
        this._mobileMode = false;
        this._currentFilterVal = "";
        this._CREATE_OPTION_VALUE = "__CREATE__";

        // The "Create '<name>'" row is rendered as a standalone footer, a sibling of the
        // filter input and the select list, rather than as an option inside the scrollable
        // select list itself — that avoids any reliance on sticky/order CSS tricks (which
        // proved unreliable at not overlapping real rows once the list actually scrolls).
        this._createOptionElem = undefined;
        this._createOptionVisible = false;
        this._createOptionSelected = false;
        if (this._model._createOption) {
            this._createOptionElem = document.createElement("list-option");
            this._createOptionElem.classList.add("create-option");
            this._createOptionElem.style.display = "none";
            this._onCreateOptionClick = this._onCreateOptionClick.bind(this);
            this._onCreateOptionMouseEnter = this._onCreateOptionMouseEnter.bind(this);
            this._onCreateOptionMouseDown = this._onCreateOptionMouseDown.bind(this);
            this._createOptionElem.addEventListener("click", this._onCreateOptionClick);
            this._createOptionElem.addEventListener("mouseenter", this._onCreateOptionMouseEnter);
            // Without this, mousedown on this non-focusable footer hands focus to the
            // nearest focusable ancestor (the combo-box-control root), which _onBlurEvent
            // treats as an outside blur and hides the options before the click can fire.
            this._createOptionElem.addEventListener("mousedown", this._onCreateOptionMouseDown);
        }

        this._isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (this._isMobile) {
            this._selectBoxForMobile = document.createElement("select");
            this._selectBoxForMobile.classList.add("select-box-mobile");
            if (!this._model._multiple && this._model._isMobileEnabled) {
                this._mobileMode = true;
            }
        }

        this._multiEditActionCombo = undefined;
        this._tagFieldWrapper = undefined;
        if (this._model._multiEditOption && this._model._createOption) {
            // Required lazily (not at module top) to avoid a circular require with
            // SingleComboBoxControlComponent, which itself requires this file as its renderer.
            const SingleComboBox = require("./single/SingleComboBoxControlComponent");
            this._multiEditActionCombo = new SingleComboBox({
                options: this._model.multiEditActions.map((action) => ({
                    label: action,
                    value: action,
                })),
                value: this._model.multiEditAction,
                showFilter: false,
                allowClear: false,
            });
            this._multiEditActionCombo.renderer.rootElement.classList.add(
                "multi-edit-action-select"
            );
            this._onMultiEditActionValue = this._onMultiEditActionValue.bind(this);
            this._multiEditActionCombo.on("Value", this._onMultiEditActionValue);
            this._rootElem.classList.add("has-multi-edit-action");
            // Positions the tag dropdown's option popup relative to just this half of the
            // split control, instead of the full width of both dropdowns combined.
            this._tagFieldWrapper = document.createElement("div");
            this._tagFieldWrapper.classList.add("tag-field-wrapper");
        }

        let clearIcon = document.createElement("base-icon-gl");
        clearIcon.dataset.icon = "common.close";
        clearIcon.classList.add("clear-icon");

        this._clearOption = document.createElement("div");
        this._clearOption.classList.add("clear-option");
        this._clearOption.appendChild(clearIcon);

        this._filter = new InputSearchControl();

        this._selectedOptions = [];
        this._optionsOpen = false;
        this._commandKeyDown = false;
        this._onSelectedOptionClick = this._onSelectedOptionClick.bind(this);

        if (this._model._adjustWidth) {
            this._rootElem.classList.add("adjust-width");
        }

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _renderSelectList() {
        if (this._model.multiple === true) {
            this._rootElem.dataset.multiple = true;
            this._selectList = new MultipleSelectListControl({comboBoxMode: true});
        } else {
            this._selectList = new SelectListControl();
        }
    }
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            if (this._multiEditActionCombo) {
                this._multiEditActionCombo.removeListener("Value", this._onMultiEditActionValue);
                this._multiEditActionCombo.shutdown();
                this._multiEditActionCombo = undefined;
            }
            this._tagFieldWrapper = undefined;
            if (this._createOptionElem) {
                this._createOptionElem.removeEventListener("click", this._onCreateOptionClick);
                this._createOptionElem.removeEventListener(
                    "mouseenter",
                    this._onCreateOptionMouseEnter
                );
                this._createOptionElem.removeEventListener(
                    "mousedown",
                    this._onCreateOptionMouseDown
                );
            }
            this._createOptionElem = undefined;
            this._model = undefined;
            this._selectList.shutdown().then(() => {
                this._selectList = undefined;
            });
            this._filter.shutdown().then(() => {
                this._filter = undefined;
            });
            this._selectedOptions = undefined;
            this._selectionDisplay = undefined;
            this._optionContainer = undefined;
            this._optionsOpen = undefined;
            this._commandKeyDown = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._selectBoxForMobile = undefined;
            this._isMobile = undefined;
            this._previousValue = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Placeholder", (this._onPlaceholder = this._onPlaceholder.bind(this)));
        this._model.on("Autoselect", (this._onAutoselect = this._onAutoselect.bind(this)));
        this._model.on("DropUp", (this._onDropUp = this._onDropUp.bind(this)));
        this._model.on("ShowFilter", (this._onShowFilter = this._onShowFilter.bind(this)));
        this._model.on("Options", (this._onOptions = this._onOptions.bind(this)));
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._model.on(
            "MultiEditAction",
            (this._onMultiEditAction = this._onMultiEditAction.bind(this))
        );

        // blur event when it originates from the filter
        this._filter.on("UserBlur", (this._onBlurEvent = this._onBlurEvent.bind(this)));
        this._selectList.on(
            "UserValue",
            (this._onSelectListUserValue = this._onSelectListUserValue.bind(this))
        );
        this._selectList.on("UserBlur", (this._onBlurEvent = this._onBlurEvent.bind(this)));
        // Clears the create-option footer's highlight on real-option hover.
        this._selectList.on(
            "UserSelected",
            (this._onSelectListUserSelected = this._onSelectListUserSelected.bind(this))
        );
        this._selectList.renderer.rootElement.addEventListener(
            "click",
            (this._onSelectListClick = this._onSelectListClick.bind(this))
        );
        this._filter.on(
            "UserChange",
            (this._onFilterUserChange = this._onFilterUserChange.bind(this))
        );
        this._rootElem.addEventListener(
            "click",
            (this._onClickEvent = this._onClickEvent.bind(this))
        );
        this._rootElem.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._rootElem.addEventListener("blur", (this._onBlurEvent = this._onBlurEvent.bind(this)));
        this._rootElem.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
        this._clearOption.addEventListener(
            "click",
            (this._onClearEvent = this._onClearEvent.bind(this))
        );
        if (BrowserDetect.os === "Mac") {
            window.addEventListener("keyup", (this._onKeyUpEvent = this._onKeyUpEvent.bind(this)));
        }
        if (this._mobileMode) {
            this._selectBoxForMobile.addEventListener(
                "change",
                (this._onSelectListUserValue = this._onSelectListUserValue.bind(this))
            );
            this._selectBoxForMobile.addEventListener(
                "focus",
                (this._setPreviousValue = this._setPreviousValue.bind(this))
            );
        }
    }
    _unregisterListeners() {
        this._model.removeListener("Placeholder", this._onPlaceholder);
        this._model.removeListener("Autoselect", this._onAutoselect);
        this._model.removeListener("DropUp", this._onDropUp);
        this._model.removeListener("ShowFilter", this._onShowFilter);
        this._model.removeListener("Options", this._onOptions);
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._model.removeListener("MultiEditAction", this._onMultiEditAction);
        this._selectList.removeListener("UserValue", this._onSelectListUserValue);
        this._selectList.removeListener("UserSelected", this._onSelectListUserSelected);
        this._selectList.renderer.rootElement.removeEventListener("click", this._onSelectListClick);
        this._filter.removeListener("UserChange", this._onFilterUserChange);
        this._filter.removeListener("UserBlur", this._onBlurEvent);
        this._rootElem.removeEventListener("click", this._onClickEvent);
        this._rootElem.removeEventListener("focus", this._onFocusEvent);
        this._rootElem.removeEventListener("blur", this._onBlurEvent);
        this._rootElem.removeEventListener("keydown", this._onKeyDownEvent);
        this._clearOption.removeEventListener("click", this._onClearEvent);
        this._selectedOptions.forEach((option) => {
            let closeButton = option.querySelector(".close-btn");
            if (closeButton) {
                closeButton.removeEventListener("click", this._onSelectedOptionClick);
            }
        });
        document.removeEventListener("click", this._onBodyClick);
        if (BrowserDetect.os === "Mac") {
            window.removeEventListener("keyup", this._onKeyUpEvent);
        }
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            if (this._mobileMode) {
                this._rootElem.appendChild(this._selectBoxForMobile);
                return resolve();
            }

            this._rootElem.innerHTML = "";
            if (this._multiEditActionCombo) {
                this._rootElem.appendChild(this._multiEditActionCombo.renderer.rootElement);
            }
            this._optionContainer.appendChild(this._filter.renderer.rootElement);
            this._optionContainer.appendChild(this._selectList.renderer.rootElement);
            if (this._createOptionElem) {
                this._optionContainer.appendChild(this._createOptionElem);
            }
            if (this._tagFieldWrapper) {
                this._tagFieldWrapper.appendChild(this._selectionDisplay);
                this._rootElem.appendChild(this._tagFieldWrapper);
            } else {
                this._rootElem.appendChild(this._selectionDisplay);
            }
            this._setPlaceholder(this._model.placeholder);
            this._setDropUp(this._model.dropUp);
            this._setShowFilter(this._model.showFilter);
            this._setOptions(this._model.options);
            this._setAutoselect(this._model.autoselect);
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);

            if (this.adjustWidth) {
                (this._tagFieldWrapper || this._rootElem).appendChild(this._optionContainer);
            }

            resolve();
        });
        return prom;
    }

    _buildSelectedOptions(selectedValues) {
        this._selectedOptions.forEach((option) => {
            let closeButton = option.querySelector(".close-btn");
            if (closeButton) {
                closeButton.removeEventListener("click", this._onSelectedOptionClick);
            }
        });
        this._selectedOptions = [];
        this._selectionDisplay.innerHTML = "";
        selectedValues.forEach((value) => {
            this._model.options.forEach((option) => {
                if (_.isEqual(option.value, value)) {
                    let selectedValue = document.createElement("selected-option");
                    selectedValue.innerHTML = option.label;
                    selectedValue.value = option.value;
                    if (!option.disabled) {
                        let closeButton = document.createElement("div");
                        closeButton.classList.add("close-btn");
                        closeButton.addEventListener("click", this._onSelectedOptionClick);
                        selectedValue.appendChild(closeButton);
                    }
                    this._selectionDisplay.appendChild(selectedValue);
                    this._selectedOptions.push(selectedValue);
                }
            });
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    get adjustWidth() {
        return this._model._adjustWidth;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the placeholder has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onPlaceholder(newVal) {
        this._setPlaceholder(newVal);
    }

    /**
     * Fired when the autoselect state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onAutoselect(newVal) {
        this._setAutoselect(newVal);
    }

    /**
     * Fired when the drop up state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onDropUp(newVal) {
        this._setDropUp(newVal);
    }

    /**
     * Fired when the show filter state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onShowFilter(newVal) {
        this._setShowFilter(newVal);
    }

    /**
     * Fired when the options have been updated in the model
     * @param newVal {array}
     * @private
     */
    _onOptions(newVal) {
        this._setOptions(newVal);
    }

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {string|object|array|boolean|number}
     * @param oldVal {string|object|array|boolean|number}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }
    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Fired when the multi-edit action changes; re-filters so "Create" is hidden/shown
     * immediately for the "Remove" action without waiting for the next keystroke.
     * @private
     */
    _onMultiEditAction() {
        this._filterOptions(this._currentFilterVal);
    }

    /**
     * Fired when the user has changed the value of the select list
     * @param newVal {string|boolean|array|object|number}
     * @param oldVal {string|boolean|array|object|number}
     * @param event {event}
     * @private
     */
    _onSelectListUserValue(newVal, oldVal, event) {
        if (this._mobileMode) {
            event = newVal;
            oldVal = this._model._isCustomObject ? this._model._value : this._previousValue;
            /* jshint ignore:start */
            newVal = this._model._isCustomObject
                ? {
                      name: event.target.options[event.target.selectedIndex].text,
                      id: event.target.options[event.target.selectedIndex].value,
                  }
                : event.target.options[event.target.selectedIndex].value;
        }
        /* jshint ignore:end */
        let specialKey =
            event.ctrlKey === true || event.commandKey === true || event.shiftKey === true
                ? true // jshint ignore:line
                : false;
        let oldValue = _.cloneDeep(oldVal);
        this._clearFilter();
        this._model.setValue(newVal, true);
        this._model._triggerUserValue(newVal, oldValue, event);
        if (specialKey !== true) {
            this._hideOptions();
        }
    }

    /**
     * Kill event bubbling at the select list level so the click event doesn't bubble up
     * to the root element
     * @param event {event}
     * @private
     */
    _onSelectListClick(event) {
        event.stopImmediatePropagation();
    }

    _clearFilter() {
        // Programmatic setValue doesn't fire UserChange, so _currentFilterVal must be
        // reset by hand or _onMultiEditAction re-filters with text the input no longer shows.
        this._currentFilterVal = "";
        this._filter.setValue("");
        this._filterOptions("");
    }

    /**
     * Fired when the user has changed the value of the filter
     * @param newVal {string}
     * @private
     */
    _onFilterUserChange(newVal) {
        this._currentFilterVal = newVal;
        this._filterOptions(newVal);
    }

    /**
     * Fired when the control is clicked
     * @param event {Event}
     * @private
     */
    _onClickEvent(event) {
        event.stopImmediatePropagation();
        if (this._model.disabled !== true) {
            this._showOptions();
        }
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        this._model._triggerUserFocus(event);
        event.stopImmediatePropagation();
        if (this._model.disabled !== true) {
            this._showOptions();
        }
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        // Triggering blur when it originates inside the combobox
        if (!event.relatedTarget || event.relatedTarget.tagName === "COMBO-BOX-CONTROL") {
            this._model._triggerUserBlur(event);
            this._hideOptions();
        }
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        let keycode = event.keyCode,
            enterKey = 13,
            controlKey = event.ctrlKey,
            downArrow = 40,
            upArrow = 38,
            commandKey1 = 91,
            commandKey2 = 93;

        if (keycode === commandKey1 || keycode === commandKey2) {
            this._commandKeyDown = true;
        }

        if (this._optionsOpen === true) {
            let userSelectedValue = this._selectList.getUserSelected(),
                value = this._model.getValue(),
                options = this._selectList.getOptions();
            if (keycode === downArrow) {
                if (!this._createOptionSelected) {
                    const nextValue = this._adjacentEnabledValue(options, userSelectedValue, 1);
                    if (!_.isUndefined(nextValue)) {
                        this._selectList.userSelected = nextValue;
                    } else if (this._createOptionVisible) {
                        // Past the last real option - create-option footer is visually next.
                        this._createOptionSelected = true;
                        this._selectList.userSelected = undefined;
                    }
                }
                this._syncCreateOptionHighlight();
                event.stopImmediatePropagation();
            } else if (
                userSelectedValue === undefined &&
                !this._createOptionSelected &&
                keycode === enterKey
            ) {
                // Unlike down-arrow above, first Enter goes straight to create if it exists.
                if (this._createOptionVisible) {
                    this._createOptionSelected = true;
                } else {
                    const nextValue = this._adjacentEnabledValue(options, undefined, 1);
                    if (!_.isUndefined(nextValue)) {
                        this._selectList.userSelected = nextValue;
                    }
                }
                this._syncCreateOptionHighlight();
                event.stopImmediatePropagation();
            } else if (keycode === upArrow) {
                if (this._createOptionSelected) {
                    // Create is visually last - up from it goes to the last real option.
                    this._createOptionSelected = false;
                    const lastValue = this._adjacentEnabledValue(options, undefined, -1);
                    if (!_.isUndefined(lastValue)) {
                        this._selectList.userSelected = lastValue;
                    }
                } else {
                    const firstValue = this._adjacentEnabledValue(options, undefined, 1);
                    if (userSelectedValue !== firstValue) {
                        const prevValue = this._adjacentEnabledValue(
                            options,
                            userSelectedValue,
                            -1
                        );
                        if (!_.isUndefined(prevValue)) {
                            this._selectList.userSelected = prevValue;
                        }
                    }
                    // else: nothing above the first real option - up is a no-op there.
                }
                this._syncCreateOptionHighlight();
                event.stopImmediatePropagation();
            } else if (keycode === enterKey) {
                if (this._model.multiple === true) {
                    const wasCreating = this._createOptionSelected;
                    if (wasCreating) {
                        userSelectedValue = this._model.createOption(this._currentFilterVal).value;
                        // Mirrors _onCreateOptionClick; resets _createOptionSelected, hence wasCreating above.
                        this._clearFilter();
                    }
                    if (controlKey === true || this._commandKeyDown === true) {
                        let valueExists = false;
                        value.forEach((val, index) => {
                            if (val === userSelectedValue) {
                                value.splice(index, 1);
                                valueExists = true;
                            }
                        });
                        if (valueExists !== true) {
                            value.push(userSelectedValue);
                        }
                        this._model.setValue(value);
                    } else {
                        // Toggles inclusion, matching a comboBoxMode mouse click.
                        const alreadySelected = _.some(value, (val) =>
                            _.isEqual(val, userSelectedValue)
                        );
                        this._model.setValue(
                            alreadySelected
                                ? value.filter((val) => !_.isEqual(val, userSelectedValue))
                                : value.concat(userSelectedValue)
                        );
                        this._hideOptions();
                    }
                } else {
                    this._clearFilter();
                    this._model.setValue(userSelectedValue);
                    this._model._triggerUserValue(userSelectedValue, value, event);
                    this._hideOptions();
                }
            }
            event.stopImmediatePropagation();
        }

        this._model._triggerUserKeyDown(event);
    }

    /**
     * Catch the command key on a Mac
     * @param event {event}
     * @private
     */
    _onKeyUpEvent(event) {
        let keycode = event.keyCode,
            commandKey1 = 91,
            commandKey2 = 93;
        if (keycode === commandKey1 || keycode === commandKey2) {
            this._commandKeyDown = false;
        }
    }

    /**
     * Trigger a hide of the options if the user clicks out of the list.
     * @param event
     * @private
     */
    _onBodyClick(/*event*/) {
        this._hideOptions();
    }

    /**
     * Trigger a clear of the value
     * @param event
     * @private
     */
    _onClearEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        event.stopPropagation();
        if (_.isArray(oldVal)) {
            this._model.setValue([]);
            this._model._triggerUserValue([], oldVal, event);
        } else {
            this._model.setValue(undefined);
            this._model._triggerUserValue(undefined, oldVal, event);
        }
    }

    _onSelectedOptionClick(event) {
        let oldVal = _.cloneDeep(this._model.value);
        event.stopPropagation();
        if (this._model.disabled !== true) {
            let newVal = [],
                selectedValue = event.currentTarget.parentNode.value;
            this._model.value.forEach((val) => {
                if (!_.isEqual(val, selectedValue)) {
                    newVal.push(val);
                }
            });
            this._model.setValue(newVal, true);
            this._model._triggerUserValue(newVal, oldVal, event);
        }
    }

    _setPreviousValue(value) {
        this._previousValue = value.target.value;
    }

    _onMultiEditActionValue(newVal) {
        this._model.setMultiEditAction(newVal);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Next enabled option's value past currentValue (undefined starts from that end).
     * @param direction {number} 1 forward, -1 backward
     * @private
     */
    _adjacentEnabledValue(options, currentValue, direction) {
        const ordered = direction === 1 ? options : [...options].reverse();
        let pastCurrent = _.isUndefined(currentValue);
        for (const option of ordered) {
            if (!pastCurrent) {
                pastCurrent = option.value === currentValue;
                continue;
            }
            if (option.disabled !== true) {
                return option.value;
            }
        }
        return undefined;
    }

    /**
     * Show the options display
     * @private
     */
    _showOptions() {
        if (this._optionsOpen === false) {
            this._optionsOpen = true;
            this._optionContainer.classList.add("shown");
            this._rootElem.removeEventListener("blur", this._onBlurEvent);

            this._rootElem.classList.add("opened");
            if (!this.adjustWidth) {
                const appendedOptions = (this._tagFieldWrapper || this._rootElem).appendChild(
                    this._optionContainer
                );
                this.model.emit("FilterOptionsDisplayed", appendedOptions);
            }
            if (this._model.showFilter === true) {
                this._filter.focus();
            } else {
                this._selectList.focus();
            }
            document.addEventListener("click", (this._onBodyClick = this._onBodyClick.bind(this)));
            this._rootElem.addEventListener(
                "blur",
                (this._onBlurEvent = this._onBlurEvent.bind(this))
            );
        }
    }

    /**
     * Hide the options display
     * @private
     */
    _hideOptions() {
        if (this._optionsOpen) {
            this._optionsOpen = false;
            this._optionContainer.classList.remove("shown");
            this._rootElem.classList.remove("opened");
            if (!this.adjustWidth) {
                (this._tagFieldWrapper || this._rootElem).removeChild(this._optionContainer);
            }
            document.removeEventListener("click", this._onBodyClick);
        }
    }

    /**
     * Filters the displaying options based on the passed in filter
     * @param filterVal {string}
     * @private
     */
    _filterOptions(filterVal) {
        let tempOptions = [];
        if (filterVal === "") {
            tempOptions = this._model.options;
            this._setCreateOptionVisibility(false, filterVal);
        } else {
            this._model.options.forEach((option) => {
                const currentOption = Object.assign({}, option);
                if (currentOption.label.toLowerCase().includes(filterVal.toLowerCase())) {
                    if (this._model._trimOnSearch) {
                        const trimRegExp = new RegExp(this._model._trimOnSearch, "g");
                        currentOption.label = currentOption.label.replace(trimRegExp, "");
                    }
                    tempOptions.push(currentOption);
                }
            });
            const hasExactMatch = this._model.options.some(
                (option) => option.label.toLowerCase() === filterVal.toLowerCase()
            );
            const isRemoveAction =
                this._model._multiEditOption && this._model.multiEditAction === "Remove";
            this._setCreateOptionVisibility(
                this._model._createOption &&
                    !hasExactMatch &&
                    !isRemoveAction &&
                    this._model.isValidNewOptionName(filterVal),
                filterVal
            );
        }
        this._setOptions(tempOptions);
        this._selectList.value = this._model.value;
        this._selectList.userSelected = undefined;
        this._createOptionSelected = false;
    }

    /**
     * Show/hide and label the standalone create-option footer row.
     * @param shouldShow {Boolean}
     * @param filterVal {string}
     * @private
     */
    _setCreateOptionVisibility(shouldShow, filterVal) {
        if (!this._createOptionElem) {
            return;
        }
        this._createOptionVisible = shouldShow === true;
        if (this._createOptionVisible) {
            // The "Create" word is supplied by the ::before badge (see the combobox
            // theme scss) - the label here is just the quoted name, so it isn't repeated.
            this._createOptionElem.innerHTML = `'${filterVal}'`;
            this._createOptionElem.value = this._CREATE_OPTION_VALUE;
            this._createOptionElem.style.display = "";
        } else {
            this._createOptionElem.style.display = "none";
        }
        this._createOptionElem.classList.remove("user-selected");
    }

    /**
     * Keep the footer's highlight class in sync with the logical keyboard/mouse cursor.
     * @private
     */
    _syncCreateOptionHighlight() {
        if (this._createOptionElem) {
            this._createOptionElem.classList.toggle(
                "user-selected",
                this._createOptionSelected === true
            );
        }
    }

    /**
     * Fired when the create-option footer is clicked: creates the tag and adds it to
     * the current selection, mirroring the "add to selection" click semantics the
     * select list itself uses in combo-box mode.
     * @param event {Event}
     * @private
     */
    _onCreateOptionClick(event) {
        event.stopImmediatePropagation();
        const oldVal = _.cloneDeep(this._model.value);
        const newOption = this._model.createOption(this._currentFilterVal);
        const newVal = (this._model.value || []).concat(newOption.value);
        const specialKey =
            event.ctrlKey === true || event.shiftKey === true || this._commandKeyDown === true;
        this._clearFilter();
        this._model.setValue(newVal, true);
        this._model._triggerUserValue(newVal, oldVal, event);
        if (specialKey !== true) {
            this._hideOptions();
        }
    }

    /**
     * Fired when the mouse hovers the create-option footer; mirrors the real options'
     * hover-to-highlight behavior.
     * @private
     */
    _onCreateOptionMouseEnter() {
        this._selectList.userSelected = undefined;
        this._createOptionSelected = true;
        this._syncCreateOptionHighlight();
    }

    /**
     * Reverse of _onCreateOptionMouseEnter, so both rows can't be highlighted at once.
     * @param newVal {string|object|boolean|number|undefined}
     * @private
     */
    _onSelectListUserSelected(newVal) {
        if (!_.isUndefined(newVal) && this._createOptionSelected) {
            this._createOptionSelected = false;
            this._syncCreateOptionHighlight();
        }
    }

    /**
     * Keeps focus on the filter input during a footer click, so the resulting blur
     * doesn't hand focus to the combo-box-control root and close the options first
     * (see the mousedown listener registered in the constructor).
     * @param event {Event}
     * @private
     */
    _onCreateOptionMouseDown(event) {
        event.preventDefault();
    }

    /**
     * Set the inner options with the passed in array of options from the model
     * @param newVal {Array}
     * @private
     */
    _setOptions(curVal) {
        const newVal =
            this._model._maxOptions > -1 && curVal.length > this._model._maxOptions
                ? curVal // jshint ignore:line
                      .slice(0, this._model._maxOptions)
                      .concat({
                          description: "",
                          label: this._model._maxOptionsMsg,
                          value: "",
                          disabled: true,
                      })
                : curVal;
        if (this._mobileMode) {
            newVal.forEach((value) => {
                if (_.get(value, "label", false) && _.get(value, "value", false)) {
                    if (
                        value.value &&
                        _.get(value.value, "name", false) &&
                        _.get(value.value, "id", false)
                    ) {
                        const option = new Option(
                            _.get(value.value, "name", false),
                            _.get(value.value, "id", false)
                        );
                        this._selectBoxForMobile.options.add(option);
                    } else {
                        const option = new Option(
                            _.get(value, "label", false),
                            _.get(value, "value", false)
                        );
                        this._selectBoxForMobile.options.add(option);
                    }
                }
            });
        } else {
            this._selectList.options = newVal;
            if (this._model.autoselect === true) {
                this._setAutoselect(true);
            }
        }
    }

    /**
     * Set the placeholder text
     * @param newVal {string}
     * @private
     */
    _setPlaceholder(/*newVal*/) {
        if (
            _.isUndefined(this._model.value) ||
            (this._model.value instanceof Array && this._model.value.length === 0)
        ) {
            this._selectionDisplay.classList.add("placeholder");
            this._selectionDisplay.innerHTML = this._model.placeholder;
        }
    }

    /**
     * Set the first option selected, if enabled
     * @param newVal {boolean}
     * @private
     */
    _setAutoselect(newVal) {
        if (!this._model._multiple && this._model._isMobileEnabled && newVal === true) {
            if (this._isMobile) {
                newVal.forEach(() => {
                    this._selectBoxForMobile.value = this._model.options[0].value;
                });
            }
        }
        if (newVal === true && this._model.options.length > 0 && !this._model._isMobileEnabled) {
            if (
                _.isUndefined(this._model.value) ||
                (this._model.value instanceof Array && this._model.value.length === 0)
            ) {
                if (this._model.multiple === true) {
                    this._model.setValue([this._model.options[0].value]);
                } else {
                    this._model.setValue(this._model.options[0].value);
                }
            }
        }
    }

    /**
     * Drop the option display above the control, if enabled
     * @param newVal {boolean}
     * @private
     */
    _setDropUp(newVal) {
        if (newVal === true) {
            this._optionContainer.classList.add("drop-up");
        } else {
            this._optionContainer.classList.remove("drop-up");
        }
    }

    /**
     * Display the search filter, if enabled
     * @param newVal {boolean}
     * @private
     */
    _setShowFilter(newVal) {
        if (newVal === true) {
            this._optionContainer.classList.add("show-filter");
        } else {
            this._optionContainer.classList.remove("show-filter");
        }
    }

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal === true) {
            this._rootElem.setAttribute("disabled", true);
            this._rootElem.tabIndex = -1;
            this._selectList.disabled = true;
            this._filter.disabled = true;
        } else {
            this._rootElem.removeAttribute("disabled");
            this._rootElem.tabIndex = 0;
            this._selectList.disabled = false;
            this._filter.disabled = false;
        }
        if (this._multiEditActionCombo) {
            this._multiEditActionCombo.setDisabled(newVal);
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("invalid");
        } else {
            this._rootElem.classList.remove("invalid");
        }
    }

    /**
     * Set the option active that matches the new value
     * @param newVal {string|object|array|boolean|number}
     * @private
     */
    _setValue(newVal) {
        if (this._mobileMode) {
            this._selectBoxForMobile.value = newVal.id || newVal;
        } else {
            this._selectionDisplay.classList.remove("placeholder");
            if (_.isUndefined(newVal) || (newVal instanceof Array && newVal.length === 0)) {
                this._selectionDisplay.classList.add("placeholder");
                this._selectionDisplay.innerHTML = this._model.placeholder;
                let clearIcon = this._rootElem.querySelector(".clear-option");
                if (clearIcon) {
                    this._rootElem.removeChild(this._clearOption);
                }
            } else if (newVal instanceof Array) {
                this._buildSelectedOptions(newVal);
                if (this.model.getAllowClear()) {
                    this._rootElem.appendChild(this._clearOption);
                }
            } else {
                this._model.options.forEach((option) => {
                    if (_.isEqual(option.value, newVal)) {
                        let {label} = option;
                        if (this._model._trimOnSearch) {
                            const trimRegex = new RegExp(this._model._trimOnSearch, "g");
                            label = label.replace(trimRegex, "");
                        }
                        this._selectionDisplay.innerHTML = label;
                        if (this.model.getAllowClear()) {
                            this._rootElem.appendChild(this._clearOption);
                        }
                    }
                });
            }
            this._selectList.value = newVal;
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("dark");
            this._selectList.darkTheme = true;
            this._filter.darkTheme = true;
        } else {
            this._rootElem.classList.remove("dark");
            this._selectList.darkTheme = false;
            this._filter.darkTheme = false;
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
            this._selectList.theme = newVal;
            this._filter.theme = newVal;
        }
    }

    /**
     * Set or remove focus
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._rootElem.focus();
        } else {
            this._rootElem.blur();
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._rootElem.setAttribute(attrName, attrValue);
        } else {
            this._rootElem.removeAttribute(attrName);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = ComboBoxControlRenderer;
